/**
 * Detection + preference for input-constrained browsers (car / TV screens)
 * where typing a password is painful and the QR device-login flow should be
 * the default entry point.
 *
 * UA sniffing is a heuristic, so it only picks the DEFAULT — never a lock-in:
 * /signin honors an explicit `?device=0|1` override, and choosing "Sign in
 * with email" on a constrained device records a sticky opt-out so the
 * redirect never traps anyone.
 */

// Tesla firmware sends a `Tesla/<version>` token (older Model S/X:
// QtCarBrowser). The rest are the common smart-TV / console browsers.
const CONSTRAINED_UA =
  /Tesla\/|QtCarBrowser|Tizen|SMART-TV|Web0S|webOS|CrKey|PlayStation|Xbox/i;

const OPT_OUT_KEY = 'optr.device-signin-optout';

export function isConstrainedDevice(): boolean {
  return CONSTRAINED_UA.test(navigator.userAgent);
}

/**
 * Should inputs steal focus on mount/restore? Fine-pointer + hover devices
 * (desktop / laptop) expect it; touch-first devices (tablets, car screens)
 * pop an on-screen keyboard over half the viewport the moment an input is
 * focused, so they must not. The media query is the primary signal; the
 * constrained-UA check is a belt for browsers (Tesla) that report a fine
 * pointer despite being touch-only.
 */
export function shouldAutoFocusInputs(): boolean {
  if (isConstrainedDevice()) return false;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return true;
  }
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

export function hasDeviceSignInOptOut(): boolean {
  try {
    return localStorage.getItem(OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

export function setDeviceSignInOptOut(): void {
  try {
    localStorage.setItem(OPT_OUT_KEY, '1');
  } catch {
    // Storage unavailable (private mode) — the redirect stays per-visit.
  }
}

/**
 * Should /signin auto-forward to /signin/device?
 * Priority: explicit ?device= override > sticky opt-out > UA detection.
 */
export function shouldDefaultToDeviceSignIn(deviceParam: string | null): boolean {
  if (deviceParam === '1') return true;
  if (deviceParam === '0') return false;
  return isConstrainedDevice() && !hasDeviceSignInOptOut();
}
